import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ContentLayout,
  Header,
  Button,
  SpaceBetween,
  Container,
  Form,
  FormField,
  Input,
  Select,
  Textarea,
  Alert,
  Box,
  Spinner
} from '@cloudscape-design/components';
import { titleAPI } from '../services/api';
import ParentSelector from '../components/ParentSelector';

const EditTitle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [showParentSelector, setShowParentSelector] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    content_type: 'film',
    parent_id: '',
    metadata: {
      title: '',
      original_title: '',
      year: '',
      genre: [],
      synopsis: '',
      language: 'en',
      country: 'US',
      status: 'draft',
      season_number: '',
      episode_number: '',
      duration_minutes: '',
      director: '',
      actors: '',
      imdb_rating: '',
      imdb_votes: '',
      box_office: '',
      awards: '',
      poster_url: ''
    },
    external_ids: {
      dalet_id: '',
      avid_id: '',
      harmonic_id: '',
      adobe_id: '',
      nomad_id: '',
      aws_elemental_id: ''
    },
    reference_ids: {
      imdb_id: '',
      eidr_id: '',
      isan: '',
      tmdb_id: ''
    }
  });

  const contentTypes = [
    { label: 'Film', value: 'film' },
    { label: 'Series', value: 'series' },
    { label: 'Season', value: 'season' },
    { label: 'Episode', value: 'episode' },
    { label: 'Documentary', value: 'documentary' },
    { label: 'Short', value: 'short' },
    { label: 'Special', value: 'special' }
  ];

  const statusOptions = [
    { label: 'Draft', value: 'draft' },
    { label: 'In Progress', value: 'in_progress' },
    { label: 'Review', value: 'review' },
    { label: 'Approved', value: 'approved' },
    { label: 'Delivered', value: 'delivered' },
    { label: 'Archived', value: 'archived' }
  ];

  useEffect(() => {
    const loadTitle = async () => {
      try {
        const response = await titleAPI.getTitle(id);
        const title = response.data;
        
        // Populate form with existing data
        setFormData({
          content_type: title.content_type || 'film',
          parent_id: title.parent_id || '',
          metadata: {
            title: title.metadata?.title || '',
            original_title: title.metadata?.original_title || '',
            year: title.metadata?.year || '',
            genre: title.metadata?.genre || [],
            synopsis: title.metadata?.synopsis || '',
            language: title.metadata?.language || 'en',
            country: title.metadata?.country || 'US',
            status: title.metadata?.status || 'draft',
            season_number: title.metadata?.season_number || '',
            episode_number: title.metadata?.episode_number || '',
            duration_minutes: title.metadata?.duration_minutes || '',
            director: title.metadata?.director || '',
            actors: title.metadata?.actors || '',
            imdb_rating: title.metadata?.imdb_rating || '',
            imdb_votes: title.metadata?.imdb_votes || '',
            box_office: title.metadata?.box_office || '',
            awards: title.metadata?.awards || '',
            poster_url: title.metadata?.poster_url || ''
          },
          external_ids: {
            dalet_id: title.external_ids?.dalet_id || '',
            avid_id: title.external_ids?.avid_id || '',
            harmonic_id: title.external_ids?.harmonic_id || '',
            adobe_id: title.external_ids?.adobe_id || '',
            nomad_id: title.external_ids?.nomad_id || '',
            aws_elemental_id: title.external_ids?.aws_elemental_id || ''
          },
          reference_ids: {
            imdb_id: title.reference_ids?.imdb_id || '',
            eidr_id: title.reference_ids?.eidr_id || '',
            isan: title.reference_ids?.isan || '',
            tmdb_id: title.reference_ids?.tmdb_id || ''
          }
        });
      } catch (error) {
        setError('Failed to load title');
      } finally {
        setLoading(false);
      }
    };

    loadTitle();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Clean up form data
      const submitData = {
        ...formData,
        metadata: {
          ...formData.metadata,
          year: formData.metadata.year ? parseInt(formData.metadata.year) : undefined,
          season_number: formData.metadata.season_number ? parseInt(formData.metadata.season_number) : undefined,
          episode_number: formData.metadata.episode_number ? parseInt(formData.metadata.episode_number) : undefined,
          duration_minutes: formData.metadata.duration_minutes ? parseInt(formData.metadata.duration_minutes) : undefined,
          imdb_rating: formData.metadata.imdb_rating ? parseFloat(formData.metadata.imdb_rating) : undefined,
          genre: Array.isArray(formData.metadata.genre) ? 
            formData.metadata.genre.filter(g => g.trim()) : 
            formData.metadata.genre.split(',').map(g => g.trim()).filter(g => g)
        }
      };

      // Remove empty strings and undefined values
      Object.keys(submitData.external_ids).forEach(key => {
        if (!submitData.external_ids[key]) {
          delete submitData.external_ids[key];
        }
      });

      Object.keys(submitData.reference_ids).forEach(key => {
        if (!submitData.reference_ids[key]) {
          delete submitData.reference_ids[key];
        }
      });

      Object.keys(submitData.metadata).forEach(key => {
        if (submitData.metadata[key] === '' || submitData.metadata[key] === undefined) {
          delete submitData.metadata[key];
        }
      });

      if (!submitData.parent_id) {
        delete submitData.parent_id;
      }

      await titleAPI.updateTitle(id, submitData);
      setSuccess('Title updated successfully!');
      
      // Redirect after a short delay
      setTimeout(() => {
        navigate(`/titles/${id}`);
      }, 1500);
      
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update title');
    } finally {
      setSaving(false);
    }
  };

  const updateMetadata = (field, value) => {
    setFormData(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        [field]: value
      }
    }));
  };

  const updateExternalId = (system, value) => {
    setFormData(prev => ({
      ...prev,
      external_ids: {
        ...prev.external_ids,
        [system]: value
      }
    }));
  };

  const updateReferenceId = (system, value) => {
    setFormData(prev => ({
      ...prev,
      reference_ids: {
        ...prev.reference_ids,
        [system]: value
      }
    }));
  };

  const handleEnrichFromIMDb = async () => {
    if (!formData.reference_ids.imdb_id) {
      setError('Please enter an IMDb ID first');
      return;
    }

    setEnriching(true);
    setError('');
    
    try {
      const enrichData = {
        imdb_id: formData.reference_ids.imdb_id,
        content_type: formData.content_type
      };
      
      if (formData.parent_id) {
        enrichData.parent_id = formData.parent_id;
      }
      
      const response = await titleAPI.enrichFromIMDb(enrichData);
      const enrichedData = response.data;
      
      setFormData(prev => ({
        ...prev,
        metadata: {
          ...prev.metadata,
          ...enrichedData.metadata
        },
        reference_ids: {
          ...prev.reference_ids,
          ...enrichedData.reference_ids
        }
      }));
      
      setSuccess('Successfully enriched from IMDb!');
      
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to enrich from IMDb');
    } finally {
      setEnriching(false);
    }
  };

  if (loading) {
    return (
      <ContentLayout>
        <Box textAlign="center">
          <Spinner size="large" />
          <Box variant="p">Loading title...</Box>
        </Box>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          actions={
            <Button onClick={() => navigate(`/titles/${id}`)}>
              Cancel
            </Button>
          }
        >
          Edit Title
        </Header>
      }
    >
      {error && (
        <Alert type="error" dismissible onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert type="success" dismissible onDismiss={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Form
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => navigate(`/titles/${id}`)}>
                Cancel
              </Button>
              <Button variant="primary" loading={saving} type="submit">
                Save Changes
              </Button>
            </SpaceBetween>
          }
        >
          <Container header={<Box variant="h3">Basic Information</Box>}>
            <SpaceBetween size="m">
              <FormField label="Content Type" stretch>
                <Select
                  selectedOption={contentTypes.find(t => t.value === formData.content_type)}
                  onChange={({ detail }) => setFormData(prev => ({ ...prev, content_type: detail.selectedOption.value }))}
                  options={contentTypes}
                />
              </FormField>

              <FormField 
                label="Parent ID" 
                description="For seasons: parent series ID. For episodes: parent season ID"
                stretch
                secondaryControl={
                  <Button 
                    variant="normal"
                    onClick={() => setShowParentSelector(true)}
                  >
                    Browse Parents
                  </Button>
                }
              >
                <Input
                  value={formData.parent_id}
                  onChange={({ detail }) => setFormData(prev => ({ ...prev, parent_id: detail.value }))}
                  placeholder="Enter parent content ID or use Browse"
                />
              </FormField>

              <FormField label="Title" stretch>
                <Input
                  value={formData.metadata.title}
                  onChange={({ detail }) => updateMetadata('title', detail.value)}
                  placeholder="Enter title"
                />
              </FormField>

              <FormField label="Original Title" stretch>
                <Input
                  value={formData.metadata.original_title}
                  onChange={({ detail }) => updateMetadata('original_title', detail.value)}
                  placeholder="Enter original title (if different)"
                />
              </FormField>

              <FormField label="Year" stretch>
                <Input
                  value={formData.metadata.year}
                  onChange={({ detail }) => updateMetadata('year', detail.value)}
                  placeholder="2024"
                  type="number"
                />
              </FormField>

              <FormField label="Genre (comma-separated)" stretch>
                <Input
                  value={Array.isArray(formData.metadata.genre) ? 
                    formData.metadata.genre.join(', ') : 
                    formData.metadata.genre}
                  onChange={({ detail }) => updateMetadata('genre', detail.value)}
                  placeholder="drama, action, comedy"
                />
              </FormField>

              <FormField label="Synopsis" stretch>
                <Textarea
                  value={formData.metadata.synopsis}
                  onChange={({ detail }) => updateMetadata('synopsis', detail.value)}
                  placeholder="Enter synopsis"
                  rows={4}
                />
              </FormField>

              <FormField label="Language" stretch>
                <Input
                  value={formData.metadata.language}
                  onChange={({ detail }) => updateMetadata('language', detail.value)}
                  placeholder="en"
                />
              </FormField>

              <FormField label="Country" stretch>
                <Input
                  value={formData.metadata.country}
                  onChange={({ detail }) => updateMetadata('country', detail.value)}
                  placeholder="US"
                />
              </FormField>

              <FormField label="Status" stretch>
                <Select
                  selectedOption={statusOptions.find(s => s.value === formData.metadata.status)}
                  onChange={({ detail }) => updateMetadata('status', detail.selectedOption.value)}
                  options={statusOptions}
                />
              </FormField>

              {(formData.content_type === 'season' || formData.content_type === 'episode') && (
                <FormField label="Season Number" stretch>
                  <Input
                    value={formData.metadata.season_number}
                    onChange={({ detail }) => updateMetadata('season_number', detail.value)}
                    placeholder="1"
                    type="number"
                  />
                </FormField>
              )}

              {formData.content_type === 'episode' && (
                <FormField label="Episode Number" stretch>
                  <Input
                    value={formData.metadata.episode_number}
                    onChange={({ detail }) => updateMetadata('episode_number', detail.value)}
                    placeholder="1"
                    type="number"
                  />
                </FormField>
              )}

              <FormField label="Duration (minutes)" stretch>
                <Input
                  value={formData.metadata.duration_minutes}
                  onChange={({ detail }) => updateMetadata('duration_minutes', detail.value)}
                  placeholder="120"
                  type="number"
                />
              </FormField>
            </SpaceBetween>
          </Container>

          <Container header={<Box variant="h3">Cast & Crew</Box>}>
            <SpaceBetween size="m">
              <FormField label="Director" stretch>
                <Input
                  value={formData.metadata.director}
                  onChange={({ detail }) => updateMetadata('director', detail.value)}
                  placeholder="Director name"
                />
              </FormField>

              <FormField label="Actors" stretch>
                <Textarea
                  value={formData.metadata.actors}
                  onChange={({ detail }) => updateMetadata('actors', detail.value)}
                  placeholder="Main actors (comma-separated)"
                  rows={2}
                />
              </FormField>
            </SpaceBetween>
          </Container>

          <Container header={<Box variant="h3">IMDb & Ratings</Box>}>
            <SpaceBetween size="m">
              <FormField label={<span style={{textTransform: 'none'}}>IMDb Rating</span>} stretch>
                <Input
                  value={formData.metadata.imdb_rating}
                  onChange={({ detail }) => updateMetadata('imdb_rating', detail.value)}
                  placeholder="7.5"
                  type="number"
                  step="0.1"
                />
              </FormField>

              <FormField label={<span style={{textTransform: 'none'}}>IMDb Votes</span>} stretch>
                <Input
                  value={formData.metadata.imdb_votes}
                  onChange={({ detail }) => updateMetadata('imdb_votes', detail.value)}
                  placeholder="100,000"
                />
              </FormField>

              <FormField label="Box Office" stretch>
                <Input
                  value={formData.metadata.box_office}
                  onChange={({ detail }) => updateMetadata('box_office', detail.value)}
                  placeholder="$100M"
                />
              </FormField>

              <FormField label="Awards" stretch>
                <Textarea
                  value={formData.metadata.awards}
                  onChange={({ detail }) => updateMetadata('awards', detail.value)}
                  placeholder="Awards and nominations"
                  rows={2}
                />
              </FormField>

              <FormField label="Poster URL" stretch>
                <Input
                  value={formData.metadata.poster_url}
                  onChange={({ detail }) => updateMetadata('poster_url', detail.value)}
                  placeholder="https://..."
                />
              </FormField>
            </SpaceBetween>
          </Container>

          <Container header={<Box variant="h3">External IDs</Box>}>
            <SpaceBetween size="m">
              <FormField label="Dalet ID" stretch>
                <Input
                  value={formData.external_ids.dalet_id}
                  onChange={({ detail }) => updateExternalId('dalet_id', detail.value)}
                  placeholder="DAL123456"
                />
              </FormField>

              <FormField label="Avid ID" stretch>
                <Input
                  value={formData.external_ids.avid_id}
                  onChange={({ detail }) => updateExternalId('avid_id', detail.value)}
                  placeholder="AVID789"
                />
              </FormField>

              <FormField label="Harmonic ID" stretch>
                <Input
                  value={formData.external_ids.harmonic_id}
                  onChange={({ detail }) => updateExternalId('harmonic_id', detail.value)}
                  placeholder="HAR456"
                />
              </FormField>

              <FormField label="Adobe ID" stretch>
                <Input
                  value={formData.external_ids.adobe_id}
                  onChange={({ detail }) => updateExternalId('adobe_id', detail.value)}
                  placeholder="ADOBE456"
                />
              </FormField>

              <FormField label="Nomad ID" stretch>
                <Input
                  value={formData.external_ids.nomad_id}
                  onChange={({ detail }) => updateExternalId('nomad_id', detail.value)}
                  placeholder="FC789"
                />
              </FormField>

              <FormField label="AWS Elemental ID" stretch>
                <Input
                  value={formData.external_ids.aws_elemental_id}
                  onChange={({ detail }) => updateExternalId('aws_elemental_id', detail.value)}
                  placeholder="ELE123"
                />
              </FormField>
            </SpaceBetween>
          </Container>

          <Container header={<Box variant="h3">Reference IDs</Box>}>
            <SpaceBetween size="m">
              <FormField 
                label={<span style={{textTransform: 'none'}}>IMDb ID</span>} 
                stretch
                secondaryControl={
                  formData.reference_ids.imdb_id && (
                    <Button 
                      variant="normal"
                      onClick={handleEnrichFromIMDb}
                      loading={enriching}
                    >
                      Enrich from IMDb
                    </Button>
                  )
                }
              >
                <Input
                  value={formData.reference_ids.imdb_id}
                  onChange={({ detail }) => updateReferenceId('imdb_id', detail.value)}
                  placeholder="tt1234567"
                />
              </FormField>

              <FormField label="EIDR ID" stretch>
                <Input
                  value={formData.reference_ids.eidr_id}
                  onChange={({ detail }) => updateReferenceId('eidr_id', detail.value)}
                  placeholder="10.5240/XXXX-XXXX-XXXX-XXXX-XXXX-C"
                />
              </FormField>

              <FormField label="ISAN" stretch>
                <Input
                  value={formData.reference_ids.isan}
                  onChange={({ detail }) => updateReferenceId('isan', detail.value)}
                  placeholder="ISAN identifier"
                />
              </FormField>

              <FormField label="TMDB ID" stretch>
                <Input
                  value={formData.reference_ids.tmdb_id}
                  onChange={({ detail }) => updateReferenceId('tmdb_id', detail.value)}
                  placeholder="12345"
                />
              </FormField>
            </SpaceBetween>
          </Container>
        </Form>
      </form>
      
      <ParentSelector
        visible={showParentSelector}
        onDismiss={() => setShowParentSelector(false)}
        onSelect={(parent) => {
          setFormData(prev => ({ ...prev, parent_id: parent.title_id }));
          setSuccess(`Selected parent: ${parent.metadata?.title || parent.title_id}`);
        }}
        contentType={formData.content_type}
      />
    </ContentLayout>
  );
};

export default EditTitle;